import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import Dashboard from './components/Dashboard';
import Signals from './components/Signals';
import Trades from './components/Trades';
import RiskPanel from './components/RiskPanel';
import GoLiveChecklist from './components/GoLiveChecklist';
import ProfitTaking from './components/ProfitTaking';
import AuditLog from './components/AuditLog';
import Review from './components/Review';
import Settings from './components/Settings';
import Commissions from './components/Commissions';
import NewsBanner from './components/NewsBanner';
import { getHealth } from './api';
import { Activity, TrendingUp, Radio, Settings as SettingsIcon } from 'lucide-react';

type Tab = 'dashboard' | 'signals' | 'audit' | 'trades' | 'profit' | 'golive' | 'review' | 'commissions' | 'risk' | 'settings';

function AppInner() {
  const { user, loading, signOut } = useAuth();
  if (loading) return <div className="min-h-screen bg-gray-950 flex items-center justify-center text-gray-500">Loading...</div>;
  if (!user) return <Login />;
  return <AppContent signOut={signOut} />;
}

function App() {
  return <AuthProvider><AppInner /></AuthProvider>;
}

type ApiStatus = 'online' | 'offline';
type TradingMode = 'live' | 'paper' | 'demo' | 'unknown';

function AppContent({ signOut }: { signOut: () => void }) {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [apiStatus, setApiStatus] = useState<ApiStatus>('offline');
  const [tradingMode, setTradingMode] = useState<TradingMode>('unknown');

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await getHealth();
        if (cancelled) return;
        if (r.status === 200 && r.data?.status === 'ok') {
          setApiStatus('online');
          const mode = String(r.data?.trading_mode || '').toLowerCase();
          setTradingMode(mode === 'live' ? 'live' : mode === 'paper' ? 'paper' : mode === 'demo' ? 'demo' : 'unknown');
        } else {
          setApiStatus('offline');
        }
      } catch {
        if (!cancelled) setApiStatus('offline');
      }
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const isLive = apiStatus === 'online' && tradingMode === 'live';
  const modeBadge = apiStatus === 'offline'
    ? { text: 'OFFLINE', cls: 'bg-gray-800 text-gray-500 border border-gray-700' }
    : isLive
      ? { text: 'LIVE', cls: 'bg-green-900 text-green-300 border border-green-700' }
      : { text: 'PAPER', cls: 'bg-yellow-900 text-yellow-300 border border-yellow-700' };
  const dotCls = apiStatus === 'online' ? 'bg-green-400' : 'bg-red-400';
  const apiLabel = apiStatus === 'online' ? 'API Online' : 'API Offline';

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Activity },
    { id: 'signals', label: 'Signals', icon: Radio },
    // Hidden: merged into Signals Trades Taken view
  // { id: 'trades', label: 'Trades', icon: TrendingUp },
    { id: 'golive', label: 'Performance', icon: Activity },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
    // Hidden: content merged into Signals tabs
    // { id: 'audit', label: 'Audit', icon: FileText },
    // Hidden: resurface when balance >= $25K (RESURFACE_PROFIT_AT)
    // { id: 'profit', label: 'Profit', icon: DollarSign },
    // Hidden: content merged into Settings
    // { id: 'risk', label: 'Risk', icon: Shield },
    // Hidden: content merged into Model
    // { id: 'review', label: 'Review', icon: BarChart3 },
    // Hidden: commission total is a KPI on Trades
    // { id: 'commissions', label: 'Commissions', icon: Receipt },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <NewsBanner />
      <header className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex flex-wrap items-center justify-between gap-y-2">
        <div className="flex items-center gap-3 flex-shrink-0">
          <img src="/techstaccs-white-icon.png" alt="Breaker Trades" style={{height:"48px"}} />
          <span className="font-bold text-lg tracking-tight">BREAKER TRADES</span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${modeBadge.cls}`} title={`Trading mode: ${tradingMode}`}>{modeBadge.text}</span>
          <span className="flex items-center gap-1.5 text-xs text-gray-400" title={apiLabel}>
            <span className={`w-2 h-2 rounded-full ${dotCls}`} />
            <span className="hidden sm:inline">{apiLabel}</span>
          </span>
        </div>
        <nav className="flex gap-1 overflow-x-auto max-w-full -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id as Tab)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${
                tab === id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
              aria-current={tab === id ? 'page' : undefined}
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
          <button onClick={signOut} className="text-xs text-gray-500 hover:text-white ml-2 px-3 py-2 rounded-md hover:bg-gray-800 transition-colors flex-shrink-0 whitespace-nowrap font-medium">Sign out</button>
        </nav>
      </header>
      <main className="p-4 min-h-0">
        {tab === 'dashboard' && <Dashboard />}
        {tab === 'signals' && <Signals />}
        {tab === 'audit' && <AuditLog />}
        {tab === 'trades' && <Trades />}
        {tab === 'profit' && <ProfitTaking />}
        {tab === 'golive' && <GoLiveChecklist />}
        {tab === 'review' && <Review />}
        {tab === 'commissions' && <Commissions />}
        {tab === 'risk' && <RiskPanel />}
        {tab === 'settings' && <Settings />}
      </main>
    </div>
  );
}

export default App;
